import { atom, useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

/** Perceived stop progress while the naming prompt is open. */
export type StopState = 'idle' | 'stopping' | 'stopped' | 'failed';

import {
  rpaRecorderBridge,
  type RpaRecorderBridge,
  type RpaRecorderStatus,
  type SessionId,
} from '@/features/rpa/recorder/api/recorder-bridge';
import { useRecorderStatus } from '@/features/rpa/recorder/hooks/useRecorderStatus';

import { useRecordings } from '@/features/rpa/recorder/hooks/useRecordings';
import {
  isTerminalNamingSkippedDecision,
  publishRecordingStopDecision,
  shouldDeferRecordingNaming,
  subscribeRecordingStopDecision,
} from '@/features/rpa/recorder/model/recording-stop-decisions';
import type {
  RecordingEntry,
  RecordingNameRequest,
} from '@/features/rpa/recorder/model/types';
import { normalizeRecordingDescription } from '@/tauri/rpa-recorder/types';
import { toast } from '@heidi/design-system/toast';

const recordingNameRequestAtom = atom<RecordingNameRequest | null>(null);

type PendingNameSubmit = {
  readonly name: string;
  readonly description: string | null;
};

function createRecordingNameBridgeActions(
  bridge: RpaRecorderBridge,
  addUploading: (name: string, sessionId: SessionId, description: string | null) => void,
  removeRecording: (sessionId: SessionId) => void
) {
  return {
    async submitName(
      sessionId: SessionId,
      rawName: string,
      rawDescription?: string | null
    ) {
      const name = rawName.trim();
      if (!name) return false;

      const description = normalizeRecordingDescription(rawDescription);
      const result = await bridge.submitRecordingName(sessionId, name, description);
      if (!result.ok) return false;

      addUploading(name, sessionId, description);
      return true;
    },
    discardWhenReady(sessionId: SessionId) {
      void bridge.discardRecordingWhenReady(sessionId).then((result) => {
        if (result.ok) removeRecording(sessionId);
      });
    },
  };
}

function deriveStopState(
  namingRequest: RecordingNameRequest | null,
  status: RpaRecorderStatus
): StopState {
  if (!namingRequest) return 'idle';
  if (status.kind === 'stopping' && status.sessionId === namingRequest.sessionId) {
    return 'stopping';
  }
  if (status.kind === 'failed' && status.sessionId === namingRequest.sessionId) {
    return 'failed';
  }
  return 'stopped';
}

function deriveStopError(
  namingRequest: RecordingNameRequest | null,
  status: RpaRecorderStatus
): string | null {
  if (
    namingRequest &&
    status.kind === 'failed' &&
    status.sessionId === namingRequest.sessionId
  ) {
    return status.error.message;
  }
  return null;
}

export function useStopAndName(bridge: RpaRecorderBridge = rpaRecorderBridge) {
  const intl = useIntl();
  const [namingRequest, setNamingRequest] = useAtom(recordingNameRequestAtom);
  const [pendingSubmit, setPendingSubmit] = useState<PendingNameSubmit | null>(null);
  const requestRef = useRef<RecordingNameRequest | null>(namingRequest);
  const status = useRecorderStatus(bridge);
  const { addUploading, removeRecording } = useRecordings();
  const bridgeActions = useMemo(
    () => createRecordingNameBridgeActions(bridge, addUploading, removeRecording),
    [addUploading, bridge, removeRecording]
  );

  const stopState = deriveStopState(namingRequest, status);
  const stopError = deriveStopError(namingRequest, status);

  useEffect(() => {
    requestRef.current = namingRequest;
  }, [namingRequest]);

  const flushSubmit = useCallback(
    async (payload: PendingNameSubmit) => {
      const request = requestRef.current;
      if (!request) return;

      const ok = await bridgeActions.submitName(
        request.sessionId,
        payload.name,
        payload.description
      );
      if (!ok) return;

      requestRef.current = null;
      setNamingRequest(null);
      setPendingSubmit(null);
      // Tell the other window(s) that naming is resolved so they clear any
      // stale prompt for this session (and suppress a late prompt-for-name).
      publishRecordingStopDecision({
        kind: 'naming-settled',
        sessionId: request.sessionId,
      });
      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Recording saved',
          id: '0Amo60LDDY',
          description: 'Toast title shown when a recording is named and saved',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Processing in the background.',
          id: 'eRipKkP38O',
          description:
            'Toast description shown when a recording is processing after being named',
        }),
      });
    },
    [bridgeActions, intl, setNamingRequest]
  );

  useEffect(() => {
    if (stopState !== 'stopped' || !pendingSubmit) return;
    void flushSubmit(pendingSubmit);
  }, [flushSubmit, pendingSubmit, stopState]);

  const applyNameRequest = useCallback(
    (request: RecordingNameRequest) => {
      requestRef.current = request;
      setNamingRequest(request);
      setPendingSubmit(null);
    },
    [setNamingRequest]
  );

  const requestName = useCallback(
    (request: RecordingNameRequest) => {
      if (shouldDeferRecordingNaming(request.sessionId)) return;
      applyNameRequest(request);
    },
    [applyNameRequest]
  );

  const submitName = useCallback(
    async (rawName: string, rawDescription?: string | null) => {
      const request = requestRef.current;
      const name = rawName.trim();
      if (!request || !name) return;

      const payload: PendingNameSubmit = {
        name,
        description: normalizeRecordingDescription(rawDescription),
      };

      if (stopState !== 'stopped') {
        setPendingSubmit(payload);
        return;
      }

      await flushSubmit(payload);
    },
    [flushSubmit, stopState]
  );

  const cancelNaming = useCallback(() => {
    const request = requestRef.current;
    requestRef.current = null;
    setNamingRequest(null);
    setPendingSubmit(null);
    if (!request) return;
    publishRecordingStopDecision({
      kind: 'naming-settled',
      sessionId: request.sessionId,
    });
    bridgeActions.discardWhenReady(request.sessionId);
  }, [bridgeActions, setNamingRequest]);

  const retryStop = useCallback(async () => {
    const request = requestRef.current;
    if (!request) return null;
    setPendingSubmit(null);
    return bridge.stop();
  }, [bridge]);

  useEffect(() => {
    return subscribeRecordingStopDecision((decision) => {
      if (decision.kind === 'prompt-for-name') {
        // A delivered terminal decision is authoritative — never veto it
        // against the stored snapshot. Cross-webview localStorage replicates
        // lazily, so at delivery time the store can still hold the stale
        // `pending` written before the native stop; deferring here would
        // silently drop the only prompt (and with it the upload).
        applyNameRequest({
          sessionId: decision.sessionId,
          suggestedName: decision.suggestedName,
        });
        return;
      }

      if (!isTerminalNamingSkippedDecision(decision)) return;
      if (requestRef.current?.sessionId !== decision.sessionId) return;
      requestRef.current = null;
      setNamingRequest(null);
      setPendingSubmit(null);
    });
  }, [applyNameRequest, setNamingRequest]);

  return {
    isNamingPromptOpen: namingRequest !== null,
    namingRequest,
    stopState,
    stopError,
    isPendingSubmit: pendingSubmit !== null,
    requestName,
    submitName,
    cancelNaming,
    retryStop,
  };
}

export function useLatestRecordingEntry(): RecordingEntry | null {
  const { recordings } = useRecordings();
  return recordings.at(-1) ?? null;
}
